import Vue from 'vue'
import isEqual from 'lodash/isEqual'
import updateRecord from '@/utils/update-record'
import deleteRecord from '@/utils/delete-record'
import { cachedAnalyseBySection } from '@/utils/analyse'
import findIndex from 'lodash/findIndex'
import dateFns from 'date-fns'
import { contractFeeNames, isLifeInsurance, editableContractInformation } from '@/utils/contract-utils'

// API resources
import { analyseSteps } from '@/api/api-values'
import apiContracts from '@/api/modules/contracts'
import apiRealEstates from '@/api/modules/real-estates'
import apiUsers from '@/api/modules/user'
import apiRiskSurvey from '@/api/modules/risk-survey'
import apiPortfolios from '@/api/modules/portfolios'
import apiTransactions from '@/api/modules/transactions'

// Mutations
import {
  ADD_ACCOUNT_PORTFOLIOS,
  ADD_AGGREGATE_DATA,
  ADD_AGGREGATE_DATA_ESR,
  ADD_CONTRACT_COMPOSITON,
  ADD_CONTRACT_GRAPH_DATA,
  ADD_CONTRACT_REAL_ESTATE,
  ADD_PORTFOLIO,
  ADD_PORTFOLIO_ANALYSE,
  ADD_PORTFOLIO_ARBITRATION,
  ADD_PORTFOLIO_BANK_FEES,
  ADD_PORTFOLIO_COMPLETE_HISTORY,
  ADD_PORTFOLIO_CURRENT_MONTH_EXPENSES,
  ADD_PORTFOLIO_HISTORY,
  ADD_PORTFOLIO_OPTIMIZED,
  ADD_PORTFOLIO_REPARTITION,
  ADD_PORTFOLIO_REPARTITION_ESR,
  ADD_PORTFOLIO_TRANSACTIONS,
  ADD_PORTFOLIO_TRANSACTIONS_HISTORY,
  ADD_PORTFOLIOS_LIST,
  ADD_PORTFOLIOS_TARGETS,
  ADD_SURVEY_QUESTIONS,
  ADD_TRANSACTIONS_CATEGORIES_LIST,
  DELETE_CONTRACT_ARBITRATION,
  DELETE_CONTRACT_REAL_ESTATE,
  TOGGLE_ANALYSING,
  TOGGLE_NEEDS_REFRESH,
  STOP_ANALYSING,
  UPDATE_CONTRACTS_PORTFOLIO,
  UPDATE_PORTFOLIO_TRANSACTION,
  UPDATE_ANALYSE_DATE,
  RESET_MODULE
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => {
  return {
    aggregates: {},
    aggregatesESR: {},
    graphs: {},
    list: [],
    realEstates: [],
    targets: {},
    analyses: {},
    analysesDate: {},
    histories: {},
    transactions: {},
    transactionCategories: [],
    transactionsHistory: {},
    contractsRepartition: [],
    contractsRepartitionESR: [],
    questions: null,
    optimizedPortfolio: {},
    arbitrationPortfolio: {},
    analysing: false,
    completeHistory: {},
    composition: {},
    currentMonthExpenses: {},
    bankFees: {}
  }
}
const state = getDefaultState()

// ----------------------------------------------------------------------

const actions = {
  fetch (context, cached = true) {
    const { list } = context.state
    if (cached && list.length) {
      return Promise.resolve(list)
    }

    return apiUsers.fetchPortfolios()
      .then(data => {
        context.commit(ADD_PORTFOLIOS_LIST, data)
      })
  },

  fetchById (context, { id, cached = true }) {
    let portfolio = context.getters.getById(id)

    if (cached && portfolio) {
      if (!portfolio._needsRefresh) {
        // If the portfolio is already in memory just return that
        return Promise.resolve(portfolio)
      } else {
        // If the portfolio is marked for refresh fetch it from API
        return apiUsers.fetchPortfolioById(id)
          .then(p => {
            context.commit(ADD_PORTFOLIO, p)
            return p
          })
      }
    }

    // If portfolio is not in memory fetch it from API
    return apiUsers.fetchPortfolioById(id)
      .then(p => {
        context.commit(ADD_PORTFOLIO, p)
        return p
      })
  },

  fetchCompleteHistory (context, { cached = true, subset = 'all' }) {
    if (cached && context.state.completeHistory[subset]) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfolioCompleteHistory(subset)
      .then(data => context.commit(ADD_PORTFOLIO_COMPLETE_HISTORY, { data, subset }))
  },

  fetchQuestions (context, cached = true) {
    const { questions } = context.state
    if (cached && questions) {
      return Promise.resolve(questions)
    }

    return apiRiskSurvey.fetchQuestions()
      .then(({ riskSurvey }) => context.commit(ADD_SURVEY_QUESTIONS, riskSurvey.filter(({ type }) => type === 'contract')))
  },

  fetchTargets (context) {
    return apiPortfolios.fetchTargets()
      .then(data => context.commit(ADD_PORTFOLIOS_TARGETS, data))
  },

  fetchAnalyses (context, { contractId, section, cached = true }) {
    const cachedValue = cachedAnalyseBySection({
      section: section,
      analyses: context.state.analyses,
      contractId: contractId
    })
    if (cached && cachedValue) {
      return Promise.resolve(cachedValue)
    }
    context.commit(TOGGLE_ANALYSING)
    return apiUsers.fetchAnalyses(contractId, section)
      .then(data => {
        const sections = data.sections
        Object.keys(sections).forEach(dataSection => {
          context.commit(ADD_PORTFOLIO_ANALYSE, {
            contractId: contractId,
            section: dataSection,
            data: sections[dataSection]
          })
        })

        context.commit(UPDATE_ANALYSE_DATE, {
          contractId,
          date: data.analysedAt
        })
      })
  },

  fetchAnalysesAsync (context, { portfolioId, section = 'all' }) {
    const cachedValue = cachedAnalyseBySection({
      section: section,
      analyses: context.state.analyses,
      contractId: portfolioId
    })
    if (cachedValue) {
      return Promise.resolve(cachedValue)
    }

    return apiPortfolios.fetchAnalyses(portfolioId)
      .then(data => {
        if (data && !data.message) {
          const sections = data.sections
          Object.keys(sections).forEach(dataSection => {
            context.commit(ADD_PORTFOLIO_ANALYSE, {
              contractId: portfolioId,
              section: dataSection,
              data: sections[dataSection]
            })
          })
          context.commit(UPDATE_ANALYSE_DATE, {
            contractId: portfolioId,
            date: data.analysedAt
          })
        }
        return data
      })
  },

  fetchHistory (context, { contractId, from, cached = true }) {
    if (cached && context.state.histories[contractId]) {
      return Promise.resolve(context.state.histories[contractId])
    }
    return apiUsers.fetchPortfolioHistory(contractId, from)
      .then(history => {
        context.commit(ADD_PORTFOLIO_HISTORY, { contractId, history })
        return history
      })
  },

  fetchOptimize (context, { contractId, cached = true }) {
    if (cached && context.state.optimizedPortfolio[contractId]) {
      return Promise.resolve()
    }
    context.commit(TOGGLE_ANALYSING)
    return apiUsers.fetchOptimize(contractId)
      .then(data => {
        return Promise.all(
          analyseSteps
            .map(({ name }) => name)
            .slice(0, -1)
            .map(step => context.dispatch('fetchAnalyses', { contractId: data.id, section: step }))
        )
          .then(() => context.commit(ADD_PORTFOLIO_OPTIMIZED, { contractId, data }))
      })
  },

  fetchArbitration (context, { contractId, method, metric, funds, cached = false }) {
    const fullMethod = `${method}${metric ? `-${metric}` : ''}`
    if (cached && context.state.arbitrationPortfolio[contractId] &&
      context.state.arbitrationPortfolio[contractId][fullMethod]) {
      return Promise.resolve()
    }
    return apiUsers.fetchOptimize(contractId, { strategy: method, metric, investments: funds })
      .then(data => {
        return Promise.all(
          analyseSteps
            .map(({ name }) => name)
            .slice(0, -1)
            .map(step => context.dispatch('fetchAnalyses', { contractId: data.id, section: step }))
        )
          .then(() => context.commit(ADD_PORTFOLIO_ARBITRATION, { contractId, method: fullMethod, data }))
      })
  },

  fetchByAccount (context, id) {
    return apiUsers.fetchPortfoliosByAccount(id)
      .then(data => context.commit(ADD_ACCOUNT_PORTFOLIOS, data))
  },

  fetchRepartition (context, cached) {
    if (cached && context.state.contractsRepartition.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosRepartition()
      .then(({ data }) => context.commit(ADD_PORTFOLIO_REPARTITION, data))
  },

  fetchRepartitionESR (context, cached) {
    if (cached && context.state.contractsRepartitionESR.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosRepartitionESR()
      .then(({ data }) => context.commit(ADD_PORTFOLIO_REPARTITION_ESR, data))
  },

  fetchAggregateContractRepartition (context, { userId, cached = true }) {
    if (cached && context.state.contractsRepartition.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchAggregateContractRepartition(userId)
      .then(({ aggregates }) => {
        context.commit(ADD_AGGREGATE_DATA, aggregates[0])
      })
  },

  fetchAggregateContractRepartitionESR (context, { userId, cached = true }) {
    if (cached && context.state.contractsRepartitionESR.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchAggregateContractRepartitionESR(userId)
      .then(({ aggregates }) => {
        context.commit(ADD_AGGREGATE_DATA_ESR, aggregates[0])
      })
  },

  fetchAggregateTransactionsHistory ({ state, commit }, { portfolioId, userId, cached = true }) {
    if (cached && state.transactionsHistory[portfolioId]) {
      return Promise.resolve(state.transactionsHistory[portfolioId])
    }

    return apiUsers.fetchAggregateTransactionsHistory(portfolioId, userId)
      .then(({ aggregates }) => {
        return commit(ADD_PORTFOLIO_TRANSACTIONS_HISTORY, { portfolioId, data: aggregates[0].data })
      })
  },

  fetchAggregateBankFees ({ state, commit }, { portfolioId, cached = true }) {
    const minDate = dateFns.format(dateFns.subYears(new Date(), 1), 'YYYY-MM-DD')

    if (cached && state.bankFees[portfolioId]) {
      return Promise.resolve(state.bankFees[portfolioId])
    }

    return apiUsers.fetchAggregateBankFees(portfolioId, minDate)
      .then(({ aggregates }) => {
        return commit(ADD_PORTFOLIO_BANK_FEES, { portfolioId, data: aggregates[0].data })
      })
  },

  fetchAggregateCurrentMonthExpenses ({ state, commit }, { portfolioId, cached = true }) {
    const minDate = dateFns.format(dateFns.subMonths(new Date(), 1), 'YYYY-MM')

    if (cached && state.currentMonthExpenses[portfolioId]) {
      return Promise.resolve(state.currentMonthExpenses[portfolioId])
    }

    return apiUsers.fetchAggregateCurrentMonthExpenses(portfolioId, minDate)
      .then(({ aggregates }) => {
        return commit(ADD_PORTFOLIO_CURRENT_MONTH_EXPENSES, { portfolioId, data: aggregates[0].data })
      })
  },

  fetchAggregateRealestateType (context, { userId, cached = true }) {
    const { aggregates } = context.state
    if (cached && aggregates && aggregates.realestateType && aggregates.realestateType.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchAggregateRealestateType(userId)
      .then(({ aggregates }) => {
        context.commit(ADD_AGGREGATE_DATA, aggregates[0])
      })
  },

  fetchAggregateRealestateEnv (context, { userId, cached = true }) {
    const { aggregates } = context.state
    if (cached && aggregates && aggregates.realestateEnv && aggregates.realestateEnv.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchAggregateRealestateEnv(userId)
      .then(({ aggregates }) => {
        context.commit(ADD_AGGREGATE_DATA, aggregates[0])
      })
  },

  fetchEuroUCComposition (context, { subset, limit = null, cached = true }) {
    if (cached && context.state.composition[subset] && context.state.composition[subset]['risk'] &&
      context.state.composition[subset]['risk'].metadata.limit === limit) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition(subset, 'risk', limit)
      .then(data => context.commit(ADD_CONTRACT_COMPOSITON, { data, subset, type: 'risk' }))
  },

  fetchKindComposition (context, { subset, limit = null, cached = true }) {
    if (cached && context.state.composition[subset] && context.state.composition[subset]['kind'] &&
      context.state.composition[subset]['kind'].metadata.limit === limit) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition(subset, 'kind', limit)
      .then(data => context.commit(ADD_CONTRACT_COMPOSITON, { data, subset, type: 'kind' }))
  },

  fetchSpendingComposition (context, { subset, limit = null, cached = true }) {
    if (cached && context.state.composition[subset] && context.state.composition[subset]['spending'] &&
      context.state.composition[subset]['spending'].metadata.limit === limit) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition(subset, 'spending', limit)
      .then(data => context.commit(ADD_CONTRACT_COMPOSITON, { data, subset, type: 'spending' }))
  },

  fetchAssetClassComposition (context, { subset, limit = null, cached = true }) {
    if (cached && context.state.composition[subset] && context.state.composition[subset]['asset_class'] &&
      context.state.composition[subset]['asset_class'].metadata.limit === limit) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition(subset, 'asset_class', limit)
      .then(data => context.commit(ADD_CONTRACT_COMPOSITON, { data, subset, type: 'asset_class' }))
  },

  fetchSrriComposition (context, { subset, limit = null, cached = true }) {
    if (cached && context.state.composition[subset] && context.state.composition[subset]['srri'] &&
      context.state.composition[subset]['srri'].metadata.limit === limit) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition(subset, 'srri', limit)
      .then(data => context.commit(ADD_CONTRACT_COMPOSITON, { data, subset, type: 'srri' }))
  },

  fetchLoansComposition (context, cached) {
    if (cached && context.state.contractsRepartition.length) {
      return Promise.resolve()
    }
    return apiUsers.fetchPortfoliosComposition('loan', 'kind')
      .then(({ data }) => context.commit(ADD_PORTFOLIO_REPARTITION, data))
  },

  fetchTransactions (context, { contractId, page, filters = {}, cached = true }) {
    return apiPortfolios.fetchTransactions(contractId, page, filters)
      .then(data => context.commit(ADD_PORTFOLIO_TRANSACTIONS, {
        contractId,
        filters,
        data,
        replace: !context.state.transactions[contractId] || !isEqual(context.state.transactions[contractId].filters, filters)
      }))
  },

  updateTransaction ({ commit }, { data }) {
    const { id, portfolioId } = data

    return apiTransactions.updateTransaction(id, data)
      .then(data => {
        commit(UPDATE_PORTFOLIO_TRANSACTION, { id, portfolioId, ...data.transaction })
        return Promise.resolve(data)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },

  fetchTransactionCategories (context, cached = true) {
    if (cached && context.state.transactionCategories.length) {
      return Promise.resolve()
    }
    return apiTransactions.fetchTransactionCategories()
      .then(data => context.commit(ADD_TRANSACTIONS_CATEGORIES_LIST, data.data))
  },

  updateInfos (context, data) {
    const infos = data.infos
    const id = data.id

    return apiPortfolios.updateInfos(id, infos)
      .then(() => apiPortfolios.fetchById(data.id)
        .then(data => {
          context.dispatch('accounts/fetchById', { id: data.accountId }, { root: true })
          context.commit(ADD_PORTFOLIO, data)
        })
      )
  },

  updateName (context, data) {
    const { name, id } = data
    return apiPortfolios.update(id, { name })
      .then(data => {
        context.dispatch('accounts/fetchById', { id: data.accountId }, { root: true })
        context.commit(UPDATE_CONTRACTS_PORTFOLIO, data)
      })
  },
  updateRisk (context, data) {
    const { riskSurvey, id } = data
    return apiPortfolios.update(id, { riskSurvey })
      .then(data => {
        context.dispatch('accounts/fetchById', { id: data.accountId }, { root: true })
        context.commit(UPDATE_CONTRACTS_PORTFOLIO, data)
      })
  },

  updateStatus (context, data) {
    const status = data.contract.status
    const id = data.id
    return apiPortfolios.updateStatus(id, status)
      .then(() => {
        context.dispatch('accounts/fetchRepartition', false, { root: true })
        context.dispatch('accounts/fetchRepartitionESR', false, { root: true })
        context.dispatch('user/fetch', false, { root: true })
          .then(() => apiUsers.fetchPortfoliosRepartition()
            .then(({ data }) => {
              context.commit(ADD_PORTFOLIO_REPARTITION, data)
            })
          )
      })
  },

  addRealEstate (context, data) {
    return apiContracts.addRealEstate(data)
      .then(({ realEstate }) => {
        return apiContracts.estimateRealEstate(realEstate.id)
          .then(({ estimate }) => {
            realEstate.estimate = estimate
            context.commit(ADD_CONTRACT_REAL_ESTATE, realEstate)
            return realEstate
          })
      })
  },

  fetchRealEstates (context, cached = true) {
    const { realEstates } = context.state
    if (cached && realEstates.length) {
      return Promise.resolve(realEstates)
    }

    return apiContracts.fetchRealEstate()
      .then(({ realEstates }) => {
        realEstates.forEach(realEstate => {
          apiContracts.estimateRealEstate(realEstate.id)
            .then(({ estimate }) => {
              realEstate.estimate = estimate
              context.commit(ADD_CONTRACT_REAL_ESTATE, realEstate)
            })
        })
      })
  },

  fetchRealEstateById (context, { id, cached = true }) {
    let realEstate
    if (cached && (realEstate = context.getters.getRealEstateById(id))) {
      return Promise.resolve(realEstate)
    }
    return apiContracts.fetchRealEstateById(id)
      .then(({ realEstate }) => {
        return apiContracts.estimateRealEstate(id)
          .then(({ estimate }) => {
            realEstate.estimate = estimate
            context.commit(ADD_CONTRACT_REAL_ESTATE, realEstate)
            return realEstate
          })
      })
  },

  updateRealEstate (context, data) {
    const infos = data.infos
    const id = data.id

    return apiRealEstates.updateRealEstate(id, infos)
      .then(() => context.dispatch('fetchRealEstateById', { id }))
  },

  deleteRealEstate (context, id) {
    return apiContracts.deleteRealEstate(id)
      .then(() => {
        context.commit(DELETE_CONTRACT_REAL_ESTATE, id)
        context.dispatch('fetchRealEstates', false)
      })
  },

  resetArbitration (context, id) {
    context.commit(DELETE_CONTRACT_ARBITRATION, id)
  },

  fetchHistoryAllCollectiveSavings (context) {
    return apiUsers.fetchPortfoliosHistoryCollectiveSavings()
      .then(data => context.commit(ADD_CONTRACT_GRAPH_DATA, { ...data, id: 'collectiveSavings' }))
  },

  fetchHistoryAll (context) {
    return apiUsers.fetchPortfoliosHistoryAll()
      .then(data => context.commit(ADD_CONTRACT_GRAPH_DATA, { ...data, id: 'historyAll' }))
  },

  fetchHistoryWealth (context, data) {
    return apiUsers.fetchPortfoliosHistoryAll()
      .then(data => context.commit(ADD_CONTRACT_GRAPH_DATA, { ...data, id: 'historyWealth' }))
  },

  markPortfoliosForRefresh (context) {
    state.list.forEach(portfolio => context.commit(TOGGLE_NEEDS_REFRESH, portfolio.id))
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  [TOGGLE_ANALYSING] (state) {
    state.analysing = true
  },

  [TOGGLE_NEEDS_REFRESH] (state, id) {
    const i = state.list.findIndex(e => e.id === id)

    if (i !== -1) {
      Vue.set(state.list[i], '_needsRefresh', !state.list[i]._needsRefresh)
    }
  },

  [STOP_ANALYSING] (state) {
    state.analysing = false
  },

  [ADD_PORTFOLIOS_LIST] (state, data) {
    state.list = data
  },

  [ADD_PORTFOLIO] (state, data) {
    const index = findIndex(state.list, { id: data.id })
    if (state.optimizedPortfolio[data.id]) {
      state.optimizedPortfolio = {
        ...state.optimizedPortfolio,
        [data.id]: null
      }
    }
    if (state.analyses[data.id]) {
      state.analyses = {
        ...state.analyses,
        [data.id]: null
      }
    }
    if (index >= 0) {
      state.list = [
        ...state.list.slice(0, index),
        data,
        ...state.list.slice(index + 1)
      ]
    } else {
      state.list = [
        ...state.list,
        data
      ]
    }
  },

  [ADD_CONTRACT_REAL_ESTATE] (state, data) {
    const index = findIndex(state.realEstates, { id: data.id })
    if (index >= 0) {
      state.realEstates = [
        ...state.realEstates.slice(0, index),
        data,
        ...state.realEstates.slice(index + 1)
      ]
    } else {
      state.realEstates = [
        ...state.realEstates,
        data
      ]
    }
  },

  [DELETE_CONTRACT_REAL_ESTATE] ({ realEstates }, id) {
    deleteRecord(realEstates, id)
  },

  [ADD_PORTFOLIO_COMPLETE_HISTORY] (state, { data, subset }) {
    state.completeHistory = {
      ...state.completeHistory,
      [subset]: data
    }
  },

  [ADD_PORTFOLIO_OPTIMIZED] (state, { contractId, data }) {
    state.optimizedPortfolio = {
      ...state.optimizedPortfolio,
      [contractId]: data
    }
    state.analysing = false
  },

  [ADD_PORTFOLIO_ARBITRATION] (state, { contractId, method, data }) {
    state.arbitrationPortfolio = {
      ...state.arbitrationPortfolio,
      [contractId]: {
        ...(state.arbitrationPortfolio[contractId] || {}),
        [method]: data
      }
    }
  },

  [DELETE_CONTRACT_ARBITRATION] (state, contractId) {
    state.arbitrationPortfolio = {
      ...state.arbitrationPortfolio,
      [contractId]: {}
    }
  },

  [ADD_SURVEY_QUESTIONS] (state, data) {
    state.questions = data
  },

  [ADD_PORTFOLIO_HISTORY] (state, { contractId, history }) {
    state.histories = {
      ...state.histories,
      [contractId]: history
    }
  },

  [ADD_ACCOUNT_PORTFOLIOS] (state, data) {
    data.forEach(data => updateRecord(state.list, data))
    state.list = [...state.list]
  },

  [ADD_PORTFOLIOS_TARGETS] (state, data) {
    state.targets = data
  },

  [ADD_PORTFOLIO_ANALYSE] (state, { contractId, section, data }) {
    let contractAnalyses = state.analyses[contractId]
    if (!contractAnalyses) {
      contractAnalyses = state.analyses[contractId] = {}
    }

    contractAnalyses[section] = data
    state.analyses = Object.assign({}, state.analyses)
    state.analysing = false
  },

  [UPDATE_ANALYSE_DATE] (state, { contractId, date }) {
    state.analysesDate[contractId] = date
  },

  [ADD_PORTFOLIO_REPARTITION] (state, data) {
    state.contractsRepartition = data
  },

  [ADD_PORTFOLIO_REPARTITION_ESR] (state, data) {
    state.contractsRepartitionESR = data
  },

  [ADD_AGGREGATE_DATA] (state, data) {
    Vue.set(state.aggregates, data.id, data)
  },

  [ADD_AGGREGATE_DATA_ESR] (state, data) {
    Vue.set(state.aggregatesESR, data.id, data)
  },

  [ADD_CONTRACT_GRAPH_DATA] (state, data) {
    Vue.set(state.graphs, data.id, data)
  },

  [UPDATE_PORTFOLIO_TRANSACTION] (state, data) {
    const transactionIndex = state.transactions[data.portfolioId].transactions.findIndex(el => el.id === data.id)
    let transactions = state.transactions[data.portfolioId].transactions

    transactions.splice(transactionIndex, 1, data)

    Vue.set(state.transactions[data.portfolioId], 'transactions', transactions)
  },

  [ADD_PORTFOLIO_TRANSACTIONS] (state, { data, contractId, replace, filters }) {
    state.transactions = {
      ...state.transactions,
      [contractId]: (replace || !state.transactions[contractId]) ? {
        ...data,
        filters
      } : {
        ...data,
        filters,
        transactions: [
          ...state.transactions[contractId].transactions,
          ...data.transactions
            .filter(transaction => !state.transactions[contractId].transactions.find(item => item.id === transaction.id))
        ]
      }
    }
  },

  [ADD_TRANSACTIONS_CATEGORIES_LIST] (state, data) {
    state.transactionCategories = data
  },

  [ADD_PORTFOLIO_TRANSACTIONS_HISTORY] (state, { portfolioId, data }) {
    Vue.set(state.transactionsHistory, portfolioId, data)
  },

  [ADD_PORTFOLIO_BANK_FEES] (state, { portfolioId, data }) {
    let bankFees = data[0] && data[0].value ? Math.abs(data[0].value) : 0

    Vue.set(state.bankFees, portfolioId, bankFees)
  },

  [ADD_PORTFOLIO_CURRENT_MONTH_EXPENSES] (state, { portfolioId, data }) {
    let expenses = {
      previous: 0,
      current: 0
    }

    data.map(monthExpenses => {
      if (dateFns.isThisMonth(monthExpenses.segments[0])) {
        expenses.current = Math.abs(monthExpenses.value)
      } else {
        expenses.previous = Math.abs(monthExpenses.value)
      }
    })

    Vue.set(state.currentMonthExpenses, portfolioId, expenses)
  },

  [ADD_CONTRACT_COMPOSITON] (state, { data, subset, type }) {
    state.composition = {
      ...state.composition,
      [subset]: {
        ...(state.composition[subset] || {}),
        [type]: data
      }
    }
  },

  [UPDATE_CONTRACTS_PORTFOLIO] (state, data) {
    if (state.optimizedPortfolio[data.id]) {
      state.optimizedPortfolio = {
        ...state.optimizedPortfolio,
        [data.id]: null
      }
    }
    if (state.analyses[data.id]) {
      state.analyses = {
        ...state.analyses,
        [data.id]: null
      }
    }
    updateRecord(state.list, data)
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getByAccount: ({ list }) => (id) => {
    return list.filter(contract => contract.accountId === id)
  },

  getById: ({ list }) => (id) => {
    return list.find(contract => parseInt(contract.id) === parseInt(id))
  },

  getRealEstateById: ({ realEstates }) => (id) => {
    return realEstates.find(realEstate => realEstate.id === id)
  },

  getHistoryById: ({ histories }) => (id) => histories[id],

  getTargetByRisk: ({ targets }) => (risk) => {
    return targets[risk]
  },

  getAnalyseScores: ({ analyses }) => (id) => {
    const scores = {}
    let analyse = {}
    if (analyses[id]) {
      if (analyses[id].hasOwnProperty('all')) {
        analyse = analyses[id].all
      } else {
        analyse = analyses[id]
      }
      Object.keys(analyse).forEach(key => { scores[key] = analyse[key].points })
    }
    return scores
  },

  getFirstDate: ({ list }) => (id) => {
    const { history } = list.find(contract => contract.id === id)
    return history[0] ? history[0][0] : Date.now()
  },

  getTransactions: ({ transactions }) => (id) => {
    return transactions[id]
  },

  getTransactionsHistory: ({ transactionsHistory }) => (id) => {
    return transactionsHistory[id]
  },

  getCurrentMonthExpenses: ({ currentMonthExpenses }) => (id) => {
    return currentMonthExpenses[id]
  },

  getBankFees: ({ bankFees }) => (id) => {
    return bankFees[id]
  },

  getOptimizedPortfolio: ({ optimizedPortfolio }) => (id) => optimizedPortfolio[id],

  getArbitrationPortfolio: ({ arbitrationPortfolio }) => (id, method) => arbitrationPortfolio[id] && arbitrationPortfolio[id][method],

  includesLifeInsurance: ({ list }) => list.some(contract => isLifeInsurance(contract)),

  isSettingsComplete: ({ list }) => (id, isReco) => {
    const contract = list.find(contract => parseInt(contract.id) === parseInt(id))
    const { infos, recommendedRisk } = contract
    const goal = Object.keys(infos).filter(key => editableContractInformation.includes(key))
      .reduce((goal, key) => {
        goal[key] = infos[key]
        return goal
      }, {})

    const feesKeys = contractFeeNames(contract)
    const fees = Object.keys(infos).filter(key => feesKeys.includes(key))
      .reduce((fee, key) => {
        fee[key] = infos[key]
        return fee
      }, {})

    const info = {
      goal: Object.values(goal).every(field => Boolean(field) || field === 0),
      fees: Object.values(fees).every(field => Boolean(field) || field === 0),
      questions: Boolean(recommendedRisk)
    }
    if (isReco) {
      info.contractId = !!contract.infos.contractId
    }
    return info
  },

  getSubCategories: ({ transactionCategories }) => {
    let subCategories = []

    transactionCategories.map(category => {
      subCategories.push(...category.children)
    })

    return subCategories.sort((a, b) => a.name.localeCompare(b.name))
  },

  isEuroFundOnly: (state, getters) => (id) => {
    return getters.getById(id).allocationDetailed.euroFund.part === 100
  },

  lastAnalysedAt: ({ analysesDate }) => (id) => {
    return analysesDate[id]
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
